import { Stack, Text } from "@chakra-ui/react";
import { useFlag } from "@unleash/proxy-client-react";
import { Dispatch, FC, SetStateAction, useMemo } from "react";

import { UnleashFlags } from "../../../../../constants";
import { PaymentDestinationItem } from "./PaymentDestinationItem";
import { PaymentDestinationDataPending } from "./PaymentDestinationDataPending";
import { getMainPaymentMethod, isStringWithNumber } from "../../../../../utils";
import { useGetCardsQuery } from "../../../../../api/cards";
import { useGetDebtStateQuery } from "../../../../../api/debt";
import { PaymentDestinationType } from "../../../../../api/payments/types";

type Props = {
  currentAmount: string;
  nearestPaymentAmount: string | undefined;
  destination: PaymentDestinationType;
  isNearestToday: boolean;
  onChange: Dispatch<SetStateAction<PaymentDestinationType>>;
};

export const PaymentBlockPaymentDestination: FC<Props> = (props) => {
  const { currentAmount, nearestPaymentAmount, destination, isNearestToday, onChange } = props;
  const isDebtRepaymentOptionsEnabled = useFlag(UnleashFlags.debtRepaymentOptionsEnabled);

  const { data: cards, isPending: isCardsPending } = useGetCardsQuery();
  const { data: debt, isPending: isDebtPending } = useGetDebtStateQuery();

  const advanceDescription = "Средства сохраняются в аванс и спишутся в дату платежа по графику";
  const chdpDescription = "Уменьшает будущие платежи или погашает задолженность полностью";

  const fullDebtAmount = isStringWithNumber(debt?.total) ? Number(debt?.total) : 0;
  const nearestAmount = isStringWithNumber(nearestPaymentAmount) ? Number(nearestPaymentAmount) : 0;
  const hasOverdue = isStringWithNumber(debt?.overdue?.total) && Number(debt?.overdue?.total) > 0;
  const overdueAmount = Number(debt?.overdue?.total);

  const isBothDisabled
      = !currentAmount
      || Number(currentAmount) > fullDebtAmount
      || (isNearestToday && !hasOverdue && (!nearestAmount || (Number(currentAmount) <= nearestAmount)))
      || (hasOverdue && !isNearestToday && (Number(currentAmount) <= overdueAmount))
      || (isNearestToday && hasOverdue && (Number(currentAmount) <= (nearestAmount + overdueAmount)));
  const isAdvanceDisabled = isBothDisabled || Number(currentAmount) === fullDebtAmount;
  const isChdpAndPdpDisabled = isBothDisabled;

  const mainPaymentMethod = useMemo(() => {
    return getMainPaymentMethod(cards);
  }, [cards]);

  const onSelectDestination = (dest: PaymentDestinationType) => {
    if (dest !== destination) {
      onChange(dest);
    }
  };

  if (isDebtRepaymentOptionsEnabled && (isCardsPending || isDebtPending)) {
    return <PaymentDestinationDataPending />;
  }

  if (!mainPaymentMethod || !isDebtRepaymentOptionsEnabled) {
    return null;
  }

  return (
    <Stack spacing={0} mt="24px">
      <Text variant="brand" mb="8px">Назначение платежа</Text>

      <Stack spacing="8px">
        <PaymentDestinationItem
          title="Платеж по графику"
          description={advanceDescription}
          isDisabled={isAdvanceDisabled}
          isSelected={destination === "ADVANCE"}
          onClick={() => { onSelectDestination("ADVANCE"); }}
        />

        <PaymentDestinationItem
          title="Досрочное погашение"
          description={chdpDescription}
          isDisabled={isChdpAndPdpDisabled}
          isSelected={destination === "EARLY_REPAYMENT"}
          onClick={() => { onSelectDestination("EARLY_REPAYMENT"); }}
        />
      </Stack>
    </Stack>
  );
};
